export const OPEN_APP_EVENT = "gsv:open-app";

export type ThreadContext = {
  pid: string;
  workspaceId: string | null;
  cwd: string;
};

export type FilesOpenPayload = {
  device?: string;
  deviceId?: string;
  target?: string;
  path?: string;
  open?: string;
  q?: string;
  context?: ThreadContext | null;
};

export type ShellOpenPayload = {
  device?: string;
  deviceId?: string;
  target?: string;
  cwd?: string;
  context?: ThreadContext | null;
};

export type ChatOpenPayload = {
  pid: string;
  workspaceId?: string | null;
  cwd: string;
};

export type WikiOpenPayload = {
  db?: string;
  path?: string;
  mode?: "browse" | "edit" | "build" | "ingest" | "inbox";
};

export type OpenAppRequest =
  | { target: "files"; payload?: FilesOpenPayload }
  | { target: "shell"; payload?: ShellOpenPayload }
  | { target: "chat"; payload: ChatOpenPayload }
  | { target: "wiki"; payload?: WikiOpenPayload }
  | { target: string; payload?: { route?: string } };

export type OpenAppEventDetail = {
  request?: OpenAppRequest | null;
  appId?: string;
  route?: string;
  threadContext?: ThreadContext | null;
};

export type ResolvedOpenAppDetail =
  | {
      type: "app";
      appId: string;
      route?: string;
      threadContext?: ThreadContext | null;
    }
  | {
      type: "chat-process";
      threadContext: ThreadContext;
    };

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  return value as Record<string, unknown>;
}

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function readRequestedTarget(payload: Record<string, unknown> | null): string | null {
  const target = asString(payload?.device) ?? asString(payload?.deviceId) ?? asString(payload?.target);
  const normalized = target?.trim() || "";
  return normalized || null;
}

function writeParam(url: URL, key: string, value: string | undefined): void {
  const normalized = value?.trim();
  if (normalized) {
    url.searchParams.set(key, normalized);
  } else {
    url.searchParams.delete(key);
  }
}

export function normalizeThreadContext(value: unknown): ThreadContext | null {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  const pid = asString(record.pid)?.trim() || "";
  const cwd = asString(record.cwd)?.trim() || "";
  const workspaceId = asString(record.workspaceId)?.trim() || null;
  if (!pid || !cwd) {
    return null;
  }
  return { pid, cwd, workspaceId };
}

export function buildOpenAppRoute(request: OpenAppRequest, locationHref: string): string {
  const target = String(request.target ?? "").trim();
  const payload = asRecord(request.payload) ?? {};
  if (target === "files") {
    const context = normalizeThreadContext(payload.context);
    const url = new URL("/apps/files/", locationHref);
    writeParam(url, "target", readRequestedTarget(payload) ?? undefined);
    writeParam(url, "path", asString(payload.path) ?? context?.cwd ?? undefined);
    writeParam(url, "open", asString(payload.open) ?? undefined);
    writeParam(url, "q", asString(payload.q) ?? undefined);
    return `${url.pathname}${url.search}`;
  }
  if (target === "shell") {
    const context = normalizeThreadContext(payload.context);
    const url = new URL("/apps/shell/", locationHref);
    writeParam(url, "target", readRequestedTarget(payload) ?? undefined);
    writeParam(url, "cwd", asString(payload.cwd) ?? context?.cwd ?? undefined);
    return `${url.pathname}${url.search}`;
  }
  if (target === "wiki") {
    const url = new URL("/apps/wiki/", locationHref);
    writeParam(url, "db", asString(payload.db) ?? undefined);
    writeParam(url, "path", asString(payload.path) ?? undefined);
    writeParam(url, "mode", asString(payload.mode) ?? undefined);
    return `${url.pathname}${url.search}`;
  }
  const explicitRoute = asString(payload.route)?.trim();
  if (explicitRoute) {
    return explicitRoute;
  }
  return `/apps/${encodeURIComponent(target)}`;
}

export function resolveOpenAppRequest(
  request: OpenAppRequest | null | undefined,
  locationHref: string,
): ResolvedOpenAppDetail | null {
  const record = asRecord(request);
  if (!record) {
    return null;
  }
  const target = asString(record.target)?.trim() || "";
  if (!target) {
    return null;
  }
  const payload = asRecord(record.payload);
  if (target === "chat") {
    const threadContext = normalizeThreadContext(payload);
    if (threadContext) {
      return {
        type: "chat-process",
        threadContext,
      };
    }
    const route = asString(payload?.route)?.trim();
    return {
      type: "app",
      appId: "chat",
      route: route || undefined,
    };
  }
  if (target === "files") {
    return {
      type: "app",
      appId: "files",
      route: buildOpenAppRoute({ target: "files", payload: payload as FilesOpenPayload }, locationHref),
      threadContext: normalizeThreadContext(payload?.context),
    };
  }
  if (target === "shell") {
    return {
      type: "app",
      appId: "shell",
      route: buildOpenAppRoute({ target: "shell", payload: payload as ShellOpenPayload }, locationHref),
      threadContext: normalizeThreadContext(payload?.context),
    };
  }
  if (target === "wiki") {
    return {
      type: "app",
      appId: "wiki",
      route: buildOpenAppRoute({ target: "wiki", payload: payload as WikiOpenPayload }, locationHref),
    };
  }
  const route = asString(payload?.route)?.trim();
  return {
    type: "app",
    appId: target,
    route: route || undefined,
  };
}

export function resolveOpenAppDetail(
  detail: OpenAppEventDetail | null | undefined,
  locationHref: string,
): ResolvedOpenAppDetail | null {
  const fromRequest = resolveOpenAppRequest(detail?.request, locationHref);
  if (fromRequest) {
    return fromRequest;
  }
  const appId = typeof detail?.appId === "string" ? detail.appId.trim() : "";
  if (!appId) {
    return null;
  }
  const route = typeof detail?.route === "string" && detail.route.trim().length > 0 ? detail.route.trim() : undefined;
  return {
    type: "app",
    appId,
    route,
    threadContext: normalizeThreadContext(detail?.threadContext),
  };
}
